import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native"
import * as Animatable from "react-native-animatable"
import Icon from "react-native-vector-icons/AntDesign"

import Base, { API, Contentful } from "../../base"
import Gap from "./Gap"

export default function VenuePicker({
  visible = false,
  onVenueSelected,
  style,
  useDistance = false,
  onClose,
  showClose = true,
}) {
  const [venues, setVenues] = useState([])
  const [animatedIn, setAnimatedIn] = useState(false)

  const closeTouched = useCallback(() => {
    setAnimatedIn(false)
    onClose()
  }, [setAnimatedIn, onClose])

  const getVenues = useCallback(async () => {
    const _venues = await Contentful.getContent(Contentful.VENUES)
    if (_venues?.length > 0) {
      setVenues(_venues)
    }
  }, [setVenues])

  useEffect(() => {
    getVenues()
  }, [getVenues])

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={closeTouched}
      onShow={() => setAnimatedIn(true)}
      onDismiss={() => setAnimatedIn(false)}
    >
      <ScrollView
        style={[styles.main, style]}
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
      >
        <Animatable.View
          transition={"backgroundColor"}
          style={[
            styles.header,
            { backgroundColor: animatedIn ? "#00000099" : "#00000000" },
          ]}
        />
        <View style={styles.card}>
          {!!showClose && (
            <TouchableOpacity
              style={styles.closeButton}
              onPress={closeTouched}
              activeOpacity={0.8}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Icon name={"close"} color={Base.colours.appRed} size={28} />
            </TouchableOpacity>
          )}
          <Text style={styles.title}>SELECT A VENUE</Text>
          {(venues ?? []).map((venue, index) => {
            return (
              <TouchableOpacity
                style={styles.venueRow}
                key={index}
                onPress={() => onVenueSelected(venue)}
                activeOpacity={0.9}
              >
                <Text style={styles.venueIndex}>{index + 1}.</Text>
                <View style={styles.venueInfo}>
                  <Text style={styles.venueTitle}>{venue?.name}</Text>
                  <Text style={styles.venueDistance}>
                    {useDistance ? "XX miles away" : venue?.postcode},{" "}
                    {Base.functions.venueOpenStatus(venue)}
                  </Text>
                </View>
              </TouchableOpacity>
            )
          })}
          <Gap size={40} />
        </View>
      </ScrollView>
    </Modal>
  )
}

const styles = StyleSheet.create({
  main: {
    flex: 1,
  },

  container: {
    flexGrow: 1,
  },

  header: {
    height: "25%",
    width: "100%",
  },

  card: {
    backgroundColor: Base.colours.appWhite,
    borderTopLeftRadius: 40,
    borderTopRightRadius: 40,
    marginTop: -40,
    paddingHorizontal: 24,
    alignItems: "center",
  },

  closeButton: {
    position: "absolute",
    right: 22,
    top: 22,
  },

  title: {
    fontFamily: Base.fonts.appTitle,
    fontSize: 44,
    lineHeight: 44,
    color: Base.colours.appBrown,
    marginTop: 34,
    marginBottom: 20,
  },

  venueRow: {
    flexDirection: "row",
    borderBottomColor: Base.colours.appVeryLightGrey,
    borderBottomWidth: 1,
    paddingVertical: 10,
    width: "100%",
  },

  venueIndex: {
    fontFamily: Base.fonts.appTitle,
    fontSize: 27,
    lineHeight: 27,
    color: Base.colours.appBrown,
    marginRight: 10,
    marginTop: 3,
  },

  venueInfo: {
    flex: 1,
  },

  venueTitle: {
    fontFamily: Base.fonts.appTitle,
    fontSize: 30,
    lineHeight: 30,
    color: Base.colours.appRed,
    marginBottom: 6,
  },

  venueDistance: {
    fontFamily: Base.fonts.appMedium,
    fontSize: 16,
    color: Base.colours.appBrown,
  },
})
